import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { journalApi } from '../../api/journal';
import { accountsApi } from '../../api/accounts';
import { personsApi } from '../../api/persons';
import { cround, formatMoney } from '../../utils/formatMoney';
import { today } from '../../utils/formatDate';
import LoadingSpinner from '../../components/LoadingSpinner';
import DatePickerInput from '../../components/DatePickerInput';
import AIPrefillBanner from '../../components/AIPrefillBanner';
import type { CreateJournalEntryLineRequest } from '../../types/journal';
import { useDocumentSerialPreview } from '../../hooks/useDocumentSerial';
import { useAppBack, useBeforeUnload } from '../../hooks/useAppBack';
import { useIsMirror } from '../../hooks/useIsMirror';
import JournalDetail from './JournalDetail';

interface LineForm {
  account: string;
  amount: string;
  entryType: 'Debetas' | 'Kreditas';
  personId: string;
}

const emptyLine = (entryType: 'Debetas' | 'Kreditas' = 'Debetas'): LineForm => ({
  account: '',
  amount: '',
  entryType,
  personId: '',
});

// Values are the wire-format document type codes (kept as-is); labels come
// from journal.docType.<value> in the locale files.
const DOCUMENT_TYPES = ['sp', 'sf', 'sg', 'b', 'kio', 'kpo', 'sk', 'bd'];

export default function JournalEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const isEdit = !!id;
  // ADR-004: mirror workspaces (mysql, asmx-owned) are READ-ONLY — a
  // deep-linked edit route gets the read-only view instead of the form;
  // the API 403s writes anyway (defense in depth).
  const isMirror = useIsMirror();

  // Read AI pre-fill data from navigation state (ephemeral, gone on refresh)
  const aiPrefill = !isEdit ? (location.state as { aiPrefill?: { formType?: string; fields?: Record<string, unknown>; lines?: unknown[]; explanation?: string } } | null)?.aiPrefill : null;

  const [date, setDate] = useState(
    (aiPrefill?.fields?.date as string) || today()
  );
  const [documentNumber, setDocumentNumber] = useState(
    (aiPrefill?.fields?.documentNumber as string) || ''
  );
  const [content, setContent] = useState(
    (aiPrefill?.fields?.content as string) || ''
  );
  const [documentType, setDocumentType] = useState(
    (aiPrefill?.fields?.documentType as string) || 'sp'
  );
  const [personId, setPersonId] = useState(
    aiPrefill?.fields?.personId != null ? String(aiPrefill.fields.personId) : ''
  );
  const [lines, setLines] = useState<LineForm[]>(() => {
    if (aiPrefill?.lines && Array.isArray(aiPrefill.lines) && aiPrefill.lines.length > 0) {
      return aiPrefill.lines.map((l: unknown) => {
        const line = l as Record<string, unknown>;
        return {
          account: line.account != null ? String(line.account) : '',
          amount: line.amount != null ? String(line.amount) : '',
          entryType: (line.entryType as 'Debetas' | 'Kreditas') || 'Debetas',
          personId: line.personId != null ? String(line.personId) : '',
        };
      });
    }
    return [emptyLine('Debetas'), emptyLine('Kreditas')];
  });
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [aiPrefilled, setAiPrefilled] = useState(!!aiPrefill);
  // Finding 35: unsaved-entry guard. Dirty is set by USER edits only — the
  // onChange on the form element is event delegation (fires on real input
  // events, never on the programmatic populate/auto-number setState).
  const [dirty, setDirty] = useState(false);
  useAppBack({ enabled: true, blocking: dirty });
  useBeforeUnload(dirty);

  // Auto-numbering from document serial (only for new entries)
  const { previewNumber: serialPreview, autoSerialCode } = useDocumentSerialPreview('JournalEntry', date);

  // Auto-fill document number for new entries when serial is available
  const [serialAutoFilled, setSerialAutoFilled] = useState(false);
  useEffect(() => {
    if (!isEdit && serialPreview && !documentNumber && !aiPrefill?.fields?.documentNumber && !serialAutoFilled) {
      setDocumentNumber(serialPreview);
      setSerialAutoFilled(true);
    }
  }, [serialPreview, isEdit, documentNumber, aiPrefill, serialAutoFilled]);

  // Fetch accounts for the account selector
  const { data: accounts } = useQuery({
    queryKey: ['accounts-all'],
    queryFn: () => accountsApi.list({ limit: 500 }),
  });

  // Fetch persons for the person selector
  const { data: persons } = useQuery({
    queryKey: ['persons-all'],
    queryFn: () => personsApi.list({ limit: 500 }),
  });

  // For edit mode: load existing entry
  const { data: existingEntry, isLoading: loadingEntry } = useQuery({
    queryKey: ['journal-entry', id],
    queryFn: () => journalApi.getById(Number(id)),
    enabled: isEdit,
  });

  // Populate form when existing entry loads
  useEffect(() => {
    if (existingEntry) {
      setDate(existingEntry.date?.split('T')[0] ?? today());
      setDocumentNumber(existingEntry.documentNumber);
      setContent(existingEntry.content);
      setDocumentType(existingEntry.documentType);
      setPersonId(existingEntry.personId?.toString() ?? '');
      if (existingEntry.lines.length > 0) {
        setLines(
          existingEntry.lines.map((l) => ({
            account: l.account.toString(),
            amount: l.amount.toString(),
            entryType: l.entryType as 'Debetas' | 'Kreditas',
            personId: l.personId?.toString() ?? '',
          }))
        );
      }
    }
  }, [existingEntry]);

  // Computed totals
  const totals = useMemo(() => {
    let totalDebit = 0;
    let totalCredit = 0;
    for (const line of lines) {
      const amount = parseFloat(line.amount) || 0;
      if (line.entryType === 'Debetas') {
        totalDebit += amount;
      } else {
        totalCredit += amount;
      }
    }
    totalDebit = cround(totalDebit);
    totalCredit = cround(totalCredit);
    return { totalDebit, totalCredit };
  }, [lines]);

  const isBalanced = Math.abs(totals.totalDebit - totals.totalCredit) < 0.005;
  const hasLines = lines.some((l) => l.account && parseFloat(l.amount) > 0);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: {
      date: string;
      documentNumber: string;
      content: string;
      documentType: string;
      personId: number | null;
      lines: CreateJournalEntryLineRequest[];
    }) => journalApi.create(data),
    onSuccess: (newId) => {
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      navigate(`/journal/${newId}`);
    },
    onError: (err: Error) => setError(err.message),
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: {
      id: number;
      date: string;
      documentNumber: string;
      content: string;
      documentType: string;
      personId: number | null;
      lines: CreateJournalEntryLineRequest[];
    }) => journalApi.update(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      queryClient.invalidateQueries({ queryKey: ['journal-entry', id] });
      navigate(`/journal/${id}`);
    },
    onError: (err: Error) => setError(err.message),
  });

  // Line management
  const updateLine = (index: number, field: keyof LineForm, value: string) => {
    setLines((prev) => {
      const next = [...prev];
      const current = next[index];
      if (current) {
        next[index] = { account: current.account, amount: current.amount, entryType: current.entryType, personId: current.personId, [field]: value } as LineForm;
      }
      return next;
    });
  };

  const addDebitLine = () => setLines((prev) => [...prev, emptyLine('Debetas')]);
  const addCreditLine = () => setLines((prev) => [...prev, emptyLine('Kreditas')]);

  const removeLine = (index: number) => {
    if (lines.length <= 2) return;
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  // Validation & submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setWarning('');

    if (!documentNumber.trim()) {
      setError(t('journal.docRequired'));
      return;
    }
    if (!content.trim()) {
      setError(t('journal.contentRequired'));
      return;
    }
    if (!hasLines) {
      setError(t('journal.lineWithAccountRequired'));
      return;
    }

    // Check that all filled lines have accounts
    const filledLines = lines.filter((l) => parseFloat(l.amount) > 0);
    for (const line of filledLines) {
      if (!line.account) {
        setError(t('journal.everyLineMustHaveAccount'));
        return;
      }
    }

    if (!isBalanced) {
      setError(
        t('journal.entryNotBalanced', {
          debit: formatMoney(totals.totalDebit, false),
          credit: formatMoney(totals.totalCredit, false),
          difference: formatMoney(totals.totalDebit - totals.totalCredit, false),
        })
      );
      return;
    }

    // Warn about future dates
    if (date > today()) {
      setWarning(t('journal.futureDateWarning'));
    }

    // Build the request lines
    const requestLines: CreateJournalEntryLineRequest[] = filledLines.map((l) => ({
      account: parseInt(l.account) || 0,
      amount: cround(parseFloat(l.amount) || 0),
      entryType: l.entryType,
      personId: l.personId ? parseInt(l.personId) : null,
    }));

    const payload = {
      date,
      documentNumber: documentNumber.trim(),
      content: content.trim(),
      documentType,
      personId: personId ? parseInt(personId) : null,
      lines: requestLines,
      autoSerialCode: !documentNumber.trim() && autoSerialCode ? autoSerialCode : undefined,
    };

    if (isEdit) {
      updateMutation.mutate({ id: Number(id), ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (isEdit && loadingEntry) return <LoadingSpinner />;

  // Mirror gate (after all hooks): edit → read-only view; new → hint only.
  if (isMirror) {
    if (isEdit) return <JournalDetail />;
    return (
      <div className="max-w-5xl space-y-4">
        <h1 className="text-xl font-bold text-gray-900">{t('journal.newJournalEntry')}</h1>
        <p className="text-sm text-gray-500">{t('layout.readOnly')}</p>
      </div>
    );
  }

  // Build a lookup for account names
  const accountLookup = new Map<number, string>();
  for (const a of accounts ?? []) {
    accountLookup.set(a.accountNumber, a.name);
  }

  const debitLines = lines.filter((l) => l.entryType === 'Debetas');
  const creditLines = lines.filter((l) => l.entryType === 'Kreditas');

  return (
    <div className="max-w-5xl space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">
          {isEdit
            ? t('journal.editEntry', { id: existingEntry?.documentNumber ?? id })
            : t('journal.newJournalEntry')}
        </h1>
        <button
          className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          onClick={() => navigate(-1)}
        >
          {t('common.cancel')}
        </button>
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>
      )}
      {warning && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-700">{warning}</div>
      )}

      {aiPrefilled && (
        <AIPrefillBanner
          explanation={aiPrefill?.explanation}
          onDismiss={() => setAiPrefilled(false)}
        />
      )}

      <form onChange={() => setDirty(true)} onSubmit={handleSubmit} className="space-y-4">
        {/* Header fields */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <DatePickerInput
              label={t('common.date')}
              value={date}
              onChange={setDate}
              required
            />
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('journal.documentNumber')}</label>
              <input
                type="text"
                className={`w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md ${!documentNumber && serialPreview ? 'placeholder:text-gray-400' : ''}`}
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                placeholder={serialPreview || t('journal.docNumberPlaceholder')}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('journal.documentType')}</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
              >
                {DOCUMENT_TYPES.map((value) => (
                  <option key={value} value={value}>{t(`journal.docType.${value}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">{t('common.person')}</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={personId}
                onChange={(e) => setPersonId(e.target.value)}
              >
                <option value="">{t('common.noPerson')}</option>
                {persons?.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-4">
              <label className="block text-xs text-gray-500 mb-1">{t('common.content')}</label>
              <input
                type="text"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t('journal.contentPlaceholder')}
                required
              />
            </div>
          </div>
        </div>

        {/* Lines - two column layout: Debits | Credits */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Debit lines */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-4 py-2 border-b border-gray-200 flex justify-between items-center">
              <h2 className="font-semibold text-sm text-primary-700">
                {t('journal.debit')}
                <span className="ml-2 font-normal text-gray-500">
                  {formatMoney(totals.totalDebit, false)}
                </span>
              </h2>
              <button
                type="button"
                className="px-3 py-1 text-xs bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100"
                onClick={addDebitLine}
              >
                {t('journal.addDebit')}
              </button>
            </div>
            <div className="p-2 space-y-2">
              {lines.map((line, i) =>
                line.entryType === 'Debetas' ? (
                  <div key={i} className="flex gap-1.5 items-center">
                    <select
                      className="flex-1 px-1.5 py-1 text-sm border border-gray-200 rounded min-w-0"
                      value={line.account}
                      onChange={(e) => updateLine(i, 'account', e.target.value)}
                    >
                      <option value="">{t('common.account')}</option>
                      {accounts?.map((a) => (
                        <option key={a.accountNumber} value={a.accountNumber}>
                          {a.accountNumber} {a.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-24 px-1.5 py-1 text-sm border border-gray-200 rounded num"
                      placeholder="0.00"
                      value={line.amount}
                      onChange={(e) => updateLine(i, 'amount', e.target.value)}
                    />
                    <select
                      className="w-28 px-1 py-1 text-sm border border-gray-200 rounded"
                      value={line.personId}
                      onChange={(e) => updateLine(i, 'personId', e.target.value)}
                      title={t('journal.personOptional')}
                    >
                      <option value="">{t('common.noPerson')}</option>
                      {persons?.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="text-red-400 hover:text-red-600 text-sm px-1"
                      onClick={() => removeLine(i)}
                      disabled={lines.length <= 2}
                      title={t('journal.removeLine')}
                    >
                      x
                    </button>
                  </div>
                ) : null
              )}
              {debitLines.length === 0 && (
                <div className="text-xs text-gray-400 py-2 text-center">{t('journal.noDebitLines')}</div>
              )}
            </div>
          </div>

          {/* Credit lines */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-4 py-2 border-b border-gray-200 flex justify-between items-center">
              <h2 className="font-semibold text-sm text-green-700">
                {t('journal.credit')}
                <span className="ml-2 font-normal text-gray-500">
                  {formatMoney(totals.totalCredit, false)}
                </span>
              </h2>
              <button
                type="button"
                className="px-3 py-1 text-xs bg-green-50 text-green-700 rounded-md hover:bg-green-100"
                onClick={addCreditLine}
              >
                {t('journal.addCredit')}
              </button>
            </div>
            <div className="p-2 space-y-2">
              {lines.map((line, i) =>
                line.entryType === 'Kreditas' ? (
                  <div key={i} className="flex gap-1.5 items-center">
                    <select
                      className="flex-1 px-1.5 py-1 text-sm border border-gray-200 rounded min-w-0"
                      value={line.account}
                      onChange={(e) => updateLine(i, 'account', e.target.value)}
                    >
                      <option value="">{t('common.account')}</option>
                      {accounts?.map((a) => (
                        <option key={a.accountNumber} value={a.accountNumber}>
                          {a.accountNumber} {a.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-24 px-1.5 py-1 text-sm border border-gray-200 rounded num"
                      placeholder="0.00"
                      value={line.amount}
                      onChange={(e) => updateLine(i, 'amount', e.target.value)}
                    />
                    <select
                      className="w-28 px-1 py-1 text-sm border border-gray-200 rounded"
                      value={line.personId}
                      onChange={(e) => updateLine(i, 'personId', e.target.value)}
                      title={t('journal.personOptional')}
                    >
                      <option value="">{t('common.noPerson')}</option>
                      {persons?.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="text-red-400 hover:text-red-600 text-sm px-1"
                      onClick={() => removeLine(i)}
                      disabled={lines.length <= 2}
                      title={t('journal.removeLine')}
                    >
                      x
                    </button>
                  </div>
                ) : null
              )}
              {creditLines.length === 0 && (
                <div className="text-xs text-gray-400 py-2 text-center">{t('journal.noCreditLines')}</div>
              )}
            </div>
          </div>
        </div>

        {/* Balance indicator */}
        <div className={`rounded-lg border p-3 flex items-center justify-between ${
          isBalanced && hasLines
            ? 'bg-green-50 border-green-200'
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex gap-6 text-sm">
            <div>
              <span className="text-gray-500">{t('journal.totalDebit')}: </span>
              <span className="font-semibold num">{formatMoney(totals.totalDebit)}</span>
            </div>
            <div>
              <span className="text-gray-500">{t('journal.totalCredit')}: </span>
              <span className="font-semibold num">{formatMoney(totals.totalCredit)}</span>
            </div>
            <div>
              <span className="text-gray-500">{t('journal.difference')}: </span>
              <span className={`font-semibold num ${isBalanced ? 'text-green-600' : 'text-red-600'}`}>
                {formatMoney(totals.totalDebit - totals.totalCredit, false)}
              </span>
            </div>
          </div>
          <div className={`text-sm font-medium ${isBalanced && hasLines ? 'text-green-600' : 'text-gray-400'}`}>
            {isBalanced && hasLines ? t('journal.balanced') : t('journal.notBalanced')}
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            onClick={() => navigate(-1)}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {isPending
              ? t('common.saving')
              : isEdit
                ? t('journal.saveChanges')
                : t('journal.createEntry')}
          </button>
        </div>
      </form>
    </div>
  );
}
